import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';
import type { ButtonHTMLAttributes } from 'react';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center rounded-md font-medium text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary-focus focus:ring-offset-2 focus:ring-offset-layout disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary-default text-primary-foreground hover:bg-primary-hover active:bg-primary-active',
        destructive:
          'bg-error-default text-error-foreground hover:bg-error-hover active:bg-error-active',
        warning:
          'bg-warning-default text-warning-foreground hover:bg-warning-hover active:bg-warning-active',
        outline:
          'border border-border-default bg-layout text-layout-foreground hover:bg-secondary-default active:bg-secondary-hover',
        secondary:
          'bg-secondary-default text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active',
        ghost:
          'text-layout-foreground hover:bg-secondary-hover active:bg-secondary-active',
        link: 'text-layout-foreground underline-offset-4 hover:underline focus:ring-0 focus:ring-offset-0',
        disabled: 'cursor-not-allowed bg-primary-disabled text-primary-default',
        success:
          'bg-success-default text-success-foreground hover:bg-success-hover active:bg-success-active',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        xs: 'h-6 rounded-xs px-2 font-normal text-xs',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-8 w-8',
        link: 'h-fit p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  disabled,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled}
      onClick={handleClick}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
